<template>
  <transition name="career-ai-consent">
    <div
      v-if="visible"
      class="career-ai-consent-overlay"
      @click.self="decline('overlay')"
    >
      <div
        class="career-ai-consent-window"
        role="dialog"
        aria-modal="true"
        aria-labelledby="career-ai-consent-title"
      >
        <div class="cac-body">
          <h3 id="career-ai-consent-title" class="cac-title">
            AIチャットを始める前に
          </h3>
          <p class="cac-lead">
            あなたの履歴書をもとに、AI
            に相談しながら転職・キャリアの悩みを整理できます。
          </p>

          <ul class="cac-features">
            <li>キャリア相談（今の仕事を続けるか、向いている仕事など）</li>
            <li>自己分析・面接の練習</li>
            <li>履歴書づくりやヤギッシュの使い方の質問</li>
          </ul>

          <div class="cac-privacy">
            <p class="cac-privacy__title">データの取り扱い</p>
            <ul class="cac-privacy__list">
              <li>
                相談内容と履歴書の一部（職歴・学歴・資格・希望条件など）を AI
                サービスに送信します
              </li>
              <li>
                <strong
                  >氏名・生年月日・住所詳細・電話番号・メールアドレスは送信しません</strong
                >
              </li>
              <li>送信内容は AI モデルの学習には使用されません</li>
            </ul>

            <button
              type="button"
              class="cac-detail-toggle"
              :aria-expanded="detailOpen"
              aria-controls="career-ai-consent-detail"
              @click="toggleDetail"
            >
              {{ detailOpen ? '詳細を閉じる' : '詳しく見る' }}
            </button>

            <ul
              v-show="detailOpen"
              id="career-ai-consent-detail"
              class="cac-detail"
            >
              <li>
                <strong>送信される情報：</strong>
                ご相談内容と、履歴書のうち
                <em>年代・都道府県・職歴・学歴・資格・希望条件・自己PR本文</em>
                を Google 社の AI（Vertex AI Gemini）へ送信します。
              </li>
              <li>
                <strong>会話履歴の保存：</strong>
                会話履歴はサービス改善のため一定期間保存します。ユーザーご自身からは閲覧できません。退会時には削除されます。
              </li>
              <li>
                <strong>学習への利用：</strong>
                送信内容は AI モデルの学習には使用されません（Vertex AI Data Use
                Policy 準拠）。
              </li>
              <li>
                <strong>利用回数：</strong>
                1日あたり {{ dailyLimit }}
                メッセージまでご利用いただけます（翌0時にリセット）。
              </li>
              <li>
                <strong>注意事項：</strong>
                AIチャットの応答は参考情報です。医療・法律・税務などの専門助言は行いません。
              </li>
            </ul>
          </div>
        </div>

        <div class="cac-footer">
          <button
            type="button"
            class="cac-btn cac-btn--primary"
            :disabled="submitting"
            @click="emit('accept')"
          >
            {{ submitting ? '保存中…' : '同意してチャットする' }}
          </button>
          <button
            type="button"
            class="cac-btn cac-btn--text"
            @click="decline('cancel')"
          >
            今はやめる
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * AIチャット（ヤギコンシェルジュ）の利用同意ダイアログ。
 * 何ができるかを先に見せ、データの取り扱いは要点3つを常に表示、残りは「詳しく見る」で開く。
 * 表示と「詳しく見る」はここで計測する（同意・やめるの計測は呼び出し側）。
 */
import { ref, watch } from 'vue'
import { createTrackEvent } from '@/composables/ai/useCareerAiTracking'

/** やめた方法。ボタンか、ダイアログの外のタップか */
type ConsentDeclineReason = 'cancel' | 'overlay'

const props = withDefaults(
  defineProps<{
    visible?: boolean
    dailyLimit?: number
    submitting?: boolean
    /** 設置場所。計測の placement に入れる */
    placement?: string
  }>(),
  {
    visible: false,
    dailyLimit: 30,
    submitting: false,
    placement: 'mypage'
  }
)

const emit = defineEmits<{
  (e: 'accept'): void
  (e: 'decline', reason: ConsentDeclineReason): void
}>()

// 同意前はセッションが無いので gtag だけに送る（DB ミラーは uuid と session が揃うときだけ）
const trackEvent = createTrackEvent(
  () => '',
  () => ''
)

const detailOpen = ref(false)

watch(
  () => props.visible,
  (v) => {
    if (!v) return
    detailOpen.value = false
    trackEvent('career_ai_consent_view', { placement: props.placement })
  },
  { immediate: true }
)

function toggleDetail() {
  detailOpen.value = !detailOpen.value
  if (detailOpen.value) {
    trackEvent('career_ai_consent_detail_open', {
      placement: props.placement
    })
  }
}

function decline(reason: ConsentDeclineReason) {
  emit('decline', reason)
}
</script>

<style lang="scss" scoped>
$green: #008940;
$green-dark: #00702f;

.career-ai-consent-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgb(0 0 0 / 55%);
}

.career-ai-consent-window {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 40px rgb(0 0 0 / 18%);
}

.cac-body {
  flex: 1;
  padding: 22px 22px 8px;
  overflow-y: auto;
  color: #444;
  font-size: 13px;
  line-height: 1.7;
}

.cac-title {
  margin: 0 0 8px;
  color: #222;
  font-size: 18px;
  font-weight: 700;
}

.cac-lead {
  margin: 0 0 12px;
  font-size: 14px;
}

.cac-features {
  margin: 0 0 16px;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    margin-bottom: 6px;
    padding-left: 22px;
    color: #222;
    font-size: 14px;
    font-weight: 600;

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      color: $green;
      content: '✓';
    }
  }
}

.cac-privacy {
  padding: 12px 14px;
  border-radius: 8px;
  background: #f5f7f6;
}

.cac-privacy__title {
  margin: 0 0 6px;
  color: #222;
  font-size: 12px;
  font-weight: 700;
}

.cac-privacy__list,
.cac-detail {
  margin: 0;
  padding: 0 0 0 18px;

  /* グローバルのリセットで消えている点を戻す */
  list-style: disc;

  li {
    margin-bottom: 4px;
  }

  strong {
    color: #222;
  }
}

.cac-detail {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e3e7e5;

  li {
    margin-bottom: 8px;
  }

  em {
    color: $green;
    font-style: normal;
  }
}

.cac-detail-toggle {
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  color: $green;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  /* グローバルの button:hover { opacity: 0.5 } を打ち消す */
  &:hover {
    opacity: 1;
  }
}

.cac-footer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 22px 16px;
  border-top: 1px solid #eee;
}

.cac-btn {
  width: 100%;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
}

.cac-btn--primary {
  padding: 13px 18px;
  border: none;
  background: $green;
  color: #fff;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: $green-dark;
  }

  &:disabled {
    background: #b8b8b8;
    cursor: not-allowed;
  }
}

.cac-btn--text {
  padding: 8px 18px;
  border: none;
  background: none;
  color: #666;
  font-size: 13px;
  font-weight: 600;
}

.career-ai-consent-enter-active,
.career-ai-consent-leave-active {
  transition: opacity 0.18s ease;

  .career-ai-consent-window {
    transition: transform 0.18s ease;
  }
}

.career-ai-consent-enter,
.career-ai-consent-leave-to {
  opacity: 0;

  .career-ai-consent-window {
    transform: translateY(8px) scale(0.98);
  }
}

/* スマホは下から出るシートにして、ボタンを親指の届く位置に置く */
@media (width <= 767px) {
  .career-ai-consent-overlay {
    align-items: flex-end;
    padding: 0;
  }

  .career-ai-consent-window {
    max-width: none;
    max-height: 92vh;
    border-radius: 16px 16px 0 0;
  }

  .cac-footer {
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }

  .career-ai-consent-enter,
  .career-ai-consent-leave-to {
    .career-ai-consent-window {
      transform: translateY(24px);
    }
  }
}
</style>
